import { REACT_APPLICANT, typesofUsers } from "../config";

type NotifyFn = (message: string, options?: any) => void;

type LoginEnvelope = {
  data?: Record<string, any>;
  [key: string]: any;
};

const USER_TYPES = {
  landloards: [typesofUsers?.landlordThirdPartyAgent],
  subscribers: [
    typesofUsers?.subscriberUser,
    typesofUsers?.subscriberAdmin,
    typesofUsers?.agent,
    typesofUsers?.manager,
    typesofUsers?.admin,
  ],
  admins: [
    typesofUsers?.docuverusSuperAdmin,
    typesofUsers?.superAdmin,
    typesofUsers?.docuverusAdmin,
  ],
  applicants: [
    typesofUsers?.applicant,
    typesofUsers?.coApplicant,
    typesofUsers?.coSigner,
  ],
};

const includesRole = (roles: Array<string | undefined>, userRole?: string) =>
  roles
    .filter(Boolean)
    .map((role) => String(role).toLowerCase())
    .includes(String(userRole || "").toLowerCase());

const isDocUploadInvitation = (invitationType?: string) =>
  ["doc upload", "guest card"].includes(
    String(invitationType || "").trim().toLowerCase()
  );

const isDocUploadTarget = (targetUrl?: string) =>
  /^\/?admin\/(?:inviteLink\/)?docupload_/i.test(
    String(targetUrl || "").trim()
  );

const isInvitedAtsApplication = (authData?: Record<string, any>) =>
  String(authData?.applicationInviteType || "").trim().toLowerCase() === "application invite"
  && (
    String(authData?.applicationStatus || "").trim().toLowerCase() === "invited"
    || String(authData?.applicantStatus || "").trim().toLowerCase() === "invited"
    || Number(authData?.applicantStatus) === 1
  );

const getDocUploadRedirect = (targetUrl?: string, trnId?: string | number) => {
  let target = String(targetUrl || "")
    .trim()
    .replace(/^\/+/, "");

  // Email-first authentication returns the relative Docupload invitation URL.
  // Only accept paths owned by this application; never redirect to an arbitrary URL.
  if (/^inviteLink\//i.test(target)) {
    target = `admin/${target}`;
  }

  if (/^admin\/(?:inviteLink\/)?docupload_/i.test(target)) {
    const transactionId = String(trnId || "").trim();
    if (transactionId && !target.endsWith(`/${transactionId}`)) {
      target = `${target}/${transactionId}`;
    }
    return `/${target}`;
  }

  return "/admin";
};

const toEnvelope = (auth: LoginEnvelope | Record<string, any>): LoginEnvelope =>
  auth && Object.prototype.hasOwnProperty.call(auth, "data")
    ? (auth as LoginEnvelope)
    : { data: auth };

export const completeLogin = (
  auth: LoginEnvelope | Record<string, any>,
  _notify?: NotifyFn
) => {
  const envelope = toEnvelope(auth);
  const authData = envelope?.data || {};
  const userRole = authData?.userrole;

  localStorage.removeItem("RaStore.switchSubscriber");
  localStorage.removeItem("RaStore.switchSubscriberName");
  localStorage.setItem("orginalUserRole", JSON.stringify(userRole));
  localStorage.setItem("userRole", JSON.stringify(userRole));
  localStorage.setItem(
    "RaStore.Notification",
    JSON.stringify(authData?.systemNotification)
  );

  const termConditionStatus = localStorage.getItem(
    "TermAndConditionVefificationStatus"
  );

  if (includesRole(USER_TYPES.subscribers, userRole)) {
    localStorage.setItem(
      "auth",
      JSON.stringify({
        ...envelope,
        data: { ...authData, subscriberView: true },
      })
    );

    if (
      Object.prototype.hasOwnProperty.call(authData, "isPasswordChanged") &&
      !authData?.isPasswordChanged
    ) {
      return { redirectTo: "/Ats/TempForgotPassword" };
    }

    if (
      Object.prototype.hasOwnProperty.call(authData, "isTandCAccepted") &&
      !authData?.isTandCAccepted &&
      termConditionStatus !== "1"
    ) {
      return { redirectTo: "/Ats/TermsAndConditionForUserSetup" };
    }

    return { redirectTo: "/Ats" };
  }

  if (includesRole(USER_TYPES.admins, userRole)) {
    localStorage.setItem(
      "auth",
      JSON.stringify({
        ...envelope,
        data: { ...authData, subscriberView: false },
      })
    );

    if (!authData?.isPasswordChanged) {
      return { redirectTo: "/Ats/TempForgotPassword" };
    }

    if (!authData?.isTandCAccepted) {
      return { redirectTo: "/Ats/TermsAndConditionForUserSetup" };
    }

    return { redirectTo: "/Ats" };
  }

  if (includesRole(USER_TYPES.landloards, userRole)) {
    localStorage.setItem(
      "auth",
      JSON.stringify({
        ...envelope,
        data: { ...authData, subscriberView: false, landLoadView: true },
      })
    );

    if (!authData?.isPasswordChanged) {
      return { redirectTo: "/landloard/TempForgotPassword" };
    }

    if (!authData?.isTandCAccepted) {
      return { redirectTo: "/landloard/TermsAndConditionForUserLandLoard" };
    }

    return { redirectTo: "/landloard/landLoardInbox" };
  }

  if (includesRole(USER_TYPES.applicants, userRole)) {
    if (
      isDocUploadInvitation(authData?.applicationInviteType) ||
      isDocUploadTarget(authData?.url)
    ) {
      localStorage.setItem("auth", JSON.stringify(envelope));
      return {
        redirectTo: getDocUploadRedirect(authData?.url, authData?.trn_id),
      };
    }

    const currentUrl = new URL(window.location.href);
    const redirectParam = currentUrl.searchParams.get("viewConsentPage");
    const shouldAddRedirectParam =
      String(redirectParam).toLowerCase() === "true";
    // The selected role already carries the invitation state, so open the
    // destination directly.  Routing through /login briefly mounted the
    // shared applicant login form before its bootstrap effect redirected.
    const applicantDestination = isInvitedAtsApplication(authData)
      ? "registration"
      : "instruction";
    const urlRedirectObj = new URL(
      applicantDestination,
      `${String(REACT_APPLICANT || "").replace(/\/+$/, "")}/`
    );

    urlRedirectObj.searchParams.set("trn_id", authData?.trn_id ?? "");
    urlRedirectObj.searchParams.set("url", authData?.url ?? "");
    urlRedirectObj.searchParams.set("token", authData?.tokendata ?? "");
    urlRedirectObj.searchParams.set("email", authData?.email ?? "");
    urlRedirectObj.searchParams.set(
      "refreshToken",
      authData?.refreshtoken ?? authData?.refreshToken ?? ""
    );

    if (shouldAddRedirectParam) {
      urlRedirectObj.searchParams.set("viewConsentPage", "true");
    }

    // This is a same-tab application handoff, not a popup. Replacing the
    // current entry prevents Back from reopening the authenticated login flow.
    window.location.replace(urlRedirectObj.toString());

    return { redirectTo: false };
  }

  return { redirectTo: false };
};
